<i18n locale="en">
{
  "actions": {
    "replace": "Replace video",
    "upload": "Upload video"
  },
  "errors": {
    "conversion": "The video uploaded, but we couldn't start optimizing it. Please try again.",
    "failed": "Video conversion failed. Please try another file.",
    "missing": "Video processing finished, but the playback files are missing. Please try another file.",
    "permission": "You don't have permission to upload videos for this brand.",
    "prepare": "We couldn't prepare the video upload. Please try again.",
    "status": "The video is processing, but we couldn't check its status. Please try again.",
    "tooLarge": "Videos must be 512 MB or smaller.",
    "transfer": "The upload was interrupted. Check your connection and try again.",
    "unsupported": "Choose an MP4, WebM, MOV, or MKV video."
  },
  "status": {
    "processing": "Optimizing video and preserving audio…",
    "ready": "Video ready"
  }
}
</i18n>

<template>
  <div class="hosted-video-input">
    <input
      ref="fileInput"
      class="hosted-video-input__file"
      type="file"
      accept="video/mp4,video/webm,video/quicktime,video/x-matroska,.mp4,.webm,.mov,.mkv"
      @change="onFileSelected"
    />

    <div v-if="modelValue" class="hosted-video-input__preview">
      <img :src="modelValue.thumbnail" alt="" />
      <div class="hosted-video-input__play" aria-hidden="true" />
      <div class="hosted-video-input__status">{{ t('status.ready') }}</div>
    </div>

    <div v-else-if="isProcessing" class="hosted-video-input__processing">
      <OwnProgressSpinner />
      <span>{{ t('status.processing') }}</span>
    </div>

    <div class="hosted-video-input__actions">
      <OwnButton
        :text="modelValue ? t('actions.replace') : t('actions.upload')"
        :disabled="isProcessing"
        @click="fileInput?.click()"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'
import { useNotify } from '@/utils/useNotify'

import {
  getHostedVideoErrorKey,
  MAX_HOSTED_VIDEO_BYTES,
  type HostedVideoFailure,
} from './hostedVideoErrors'

export interface HostedVideoValue {
  mp4: string
  webm: string
  thumbnail: string
}

const props = defineProps<{
  brandId: string
  modelValue: HostedVideoValue | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: HostedVideoValue | null]
}>()

const { t } = useI18n()
const notify = useNotify()
const fileInput = ref<HTMLInputElement | null>(null)
const isProcessing = ref(false)
let pollTimer: number | null = null

const CONTENT_TYPES: Record<
  string,
  'video/mp4' | 'video/webm' | 'video/quicktime' | 'video/x-matroska'
> = {
  mkv: 'video/x-matroska',
  mov: 'video/quicktime',
  mp4: 'video/mp4',
  webm: 'video/webm',
}

const stopPolling = (): void => {
  if (pollTimer) window.clearTimeout(pollTimer)
  pollTimer = null
}

const showFailure = (error: unknown, fallback: HostedVideoFailure): void => {
  logError(error)
  stopPolling()
  isProcessing.value = false
  notify(t(`errors.${getHostedVideoErrorKey(error, fallback)}`), 'error')
}

const pollStatus = async (mediaId: string): Promise<void> => {
  let result
  try {
    result =
      await OlympusClient.growth.v2.websiteBuilder.brand.video.getVideoStatus({
        params: { brandId: props.brandId, mediaId },
      })
  } catch (error) {
    showFailure(error, 'status')
    return
  }

  if (result.status === 'failed' || result.status === 'archived') {
    isProcessing.value = false
    notify(t('errors.failed'), 'error')
    return
  }

  if (
    result.status === 'ready' &&
    result.mp4 &&
    result.webm &&
    result.thumbnail
  ) {
    isProcessing.value = false
    emit('update:modelValue', {
      mp4: result.mp4,
      thumbnail: result.thumbnail,
      webm: result.webm,
    })
    return
  }

  if (result.status === 'ready') {
    isProcessing.value = false
    notify(t('errors.missing'), 'error')
    return
  }

  pollTimer = window.setTimeout(() => pollStatus(mediaId), 3000)
}

const onFileSelected = async (event: Event): Promise<void> => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return

  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  const contentType = CONTENT_TYPES[extension]
  if (!contentType) {
    notify(t('errors.unsupported'), 'error')
    return
  }
  if (file.size > MAX_HOSTED_VIDEO_BYTES) {
    notify(t('errors.tooLarge'), 'error')
    return
  }

  stopPolling()
  isProcessing.value = true

  const upload = await OlympusClient.growth.v2.websiteBuilder.brand.video
    .uploadVideo({
      body: { contentLength: file.size, contentType },
      params: { brandId: props.brandId },
    })
    .catch((error: unknown) => {
      showFailure(error, 'prepare')
      return null
    })
  if (!upload) return

  const { headers, mediaId, uploadUrl } = upload
  try {
    const response = await fetch(uploadUrl, {
      body: file,
      headers,
      method: 'PUT',
    })
    if (!response.ok) throw new Error(`Video upload failed: ${response.status}`)
  } catch (error) {
    showFailure(error, 'transfer')
    return
  }

  try {
    await OlympusClient.growth.v2.websiteBuilder.brand.video.startVideoConversion(
      {
        params: { brandId: props.brandId, mediaId },
      }
    )
  } catch (error) {
    showFailure(error, 'conversion')
    return
  }

  await pollStatus(mediaId)
}

onUnmounted(stopPolling)
</script>

<style lang="scss" scoped>
.hosted-video-input {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__file {
    display: none;
  }

  &__preview,
  &__processing {
    align-items: center;
    aspect-ratio: 16 / 9;
    background: var(--background-secondary);
    border: 1px solid var(--divider);
    border-radius: 12px;
    display: flex;
    justify-content: center;
    overflow: hidden;
    position: relative;
  }

  &__preview img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &__play {
    align-items: center;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 999px;
    color: #111;
    display: flex;
    font-size: 18px;
    height: 52px;
    justify-content: center;
    left: 50%;
    padding-left: 3px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 52px;
  }

  &__play::before {
    border-bottom: 8px solid transparent;
    border-left: 12px solid #111;
    border-top: 8px solid transparent;
    content: '';
  }

  &__status {
    background: rgba(0, 0, 0, 0.72);
    border-radius: 999px;
    bottom: 10px;
    color: white;
    font-size: 12px;
    left: 10px;
    padding: 5px 9px;
    position: absolute;
  }

  &__processing {
    flex-direction: column;
    gap: 12px;
    min-height: 180px;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
